/**
 * LatePickupControl — Ted #11 (#914).
 *
 * Ted, 2026-09-04: *"our boarding checkout time is 10:30 a.m., and … if a dog
 * remains past 11:00 a.m., a daycare charge is added. … the app should
 * recognize whether that client has an active daycare package and alert us.
 * We could then deduct one daycare visit from the package rather than billing
 * the client the full daycare rate."*
 *
 * DESIGN-PHILOSOPHY, the principles this serves:
 *  · Counter Test — the row already says the dog is going home; this adds one
 *    sentence in plain words ("Collected 11:20 AM, after the 11:00 AM
 *    cutoff") and three tap targets. Nothing to learn, nothing to compute.
 *  · 2, the next action is obvious — the likely disposition is preselected
 *    (the package the customer already paid for, when they hold one) and the
 *    other two are one tap away.
 *  · 5, never lie about data — every time shown is the FACILITY's clock, sent
 *    by the server already formatted; the price is the tenant's own daycare
 *    rate, never a guess. When the proposal cannot be read the row says so
 *    and offers Retry instead of silently showing nothing.
 *  · 7, built for hands — radio rows are full-width labels (≥44px), the
 *    waiver note is a real text input with its own label.
 *
 * #1220 (Ted, 2026-10-09) — *"give us the option of a half day or a full day
 * and we'll just leave it like that because I can always add a discount."*
 * The single daycare charge became two: a HALF day and a FULL day, each the
 * tenant's own catalogue price × the dogs on the stay. A package covers one
 * day per dog, from a half-day package for a half day and a full-day one for
 * a full day. The desk can still change the amount afterwards with Adjust
 * price on the open invoice.
 *
 * Nothing here charges anything. The choice rides the checkout POST and the
 * SERVER re-decides lateness against its own clock before writing a line.
 */
import { useId, type JSX } from 'react';
import type { AdminCustomerPackage, AdminLatePickup } from '../../lib/api.js';
import './LatePickupControl.css';

/** #1220 — a half or a full day of daycare. */
export type LatePickupLength = 'half' | 'full';

/** The desk's choice, as the Bookings orchestrator holds it per row. */
export interface LatePickupChoice {
  readonly action: 'charge' | 'package' | 'waive';
  /** #1220 — which day the CHARGE is for. A package's length is its kind. */
  readonly length: LatePickupLength;
  /** Which package, when the customer holds more than one. */
  readonly packageId: string | null;
  /** Required by the server when `action === 'waive'`. */
  readonly waiveReason: string;
}

/** A package the desk can use, and which day it covers. */
export interface LatePickupPackageOption {
  readonly pkg: AdminCustomerPackage;
  readonly length: LatePickupLength;
}

export interface LatePickupPrice {
  readonly available: boolean;
  readonly perDogCents: number;
  readonly totalCents: number;
}

/** Everything the row offers, read once from the proposal. */
export interface LatePickupOptions {
  readonly dogCount: number;
  /** Package days a package option draws: one per dog. */
  readonly daysNeeded: number;
  readonly half: LatePickupPrice;
  readonly full: LatePickupPrice;
  /** Packages holding at least `daysNeeded` days — full-day ones first. */
  readonly packages: ReadonlyArray<LatePickupPackageOption>;
  /** Packages held that have days left, but fewer than `daysNeeded`. */
  readonly shortPackages: number;
}

/**
 * The proposal, normalised. The frontends deploy on merge and the server by
 * hand, so for a while this screen can be reading a server from BEFORE #1220:
 * no half-day fields at all. Every new field therefore has a fallback that
 * means exactly what that server will do — a full day at `chargeCents`, one
 * dog, the `packages` list as full-day ones — and the half option simply
 * does not appear. The desk is never shown an option the server would not
 * honour.
 */
export function latePickupOptions(proposal: AdminLatePickup): LatePickupOptions {
  const dogCount = Math.max(1, Math.trunc(proposal.dogCount ?? 1) || 1);
  const daysNeeded = Math.max(1, Math.trunc(proposal.packageDaysNeeded ?? dogCount) || 1);
  const fullTotal = proposal.fullDayChargeCents ?? proposal.chargeCents;
  const full: LatePickupPrice = {
    available: proposal.fullDayAvailable ?? true,
    perDogCents: proposal.fullDayChargeCentsPerDog ?? Math.round(fullTotal / dogCount),
    totalCents: fullTotal,
  };
  const half: LatePickupPrice =
    proposal.halfDayAvailable === true
      ? {
          available: true,
          perDogCents: proposal.halfDayChargeCentsPerDog ?? 0,
          totalCents: proposal.halfDayChargeCents ?? 0,
        }
      : { available: false, perDogCents: 0, totalCents: 0 };
  const held: LatePickupPackageOption[] = [
    ...(proposal.fullDayPackages ?? proposal.packages).map((pkg) => ({ pkg, length: 'full' as const })),
    ...(proposal.halfDayPackages ?? []).map((pkg) => ({ pkg, length: 'half' as const })),
  ];
  const packages = held.filter((o) => o.pkg.remainingDays >= daysNeeded);
  return { dogCount, daysNeeded, half, full, packages, shortPackages: held.length - packages.length };
}

/**
 * The opening selection.
 *
 * A full-day package is preselected when the customer holds one that covers
 * the stay — it is what they already paid for, the same argument that made
 * #908's "Apply package" toggle default ON. Otherwise the full-day charge
 * (what Ted's desk does today), or the half day when the tenant prices only
 * that. Never `waive`: waiving is a decision someone makes, not a default.
 */
export function defaultLatePickupChoice(proposal: AdminLatePickup): LatePickupChoice {
  const options = latePickupOptions(proposal);
  // Johnny (10-09): start on whichever package the client holds — a
  // full-day one first, else a half-day one — and only then on a charge.
  const heldPackage =
    options.packages.find((o) => o.length === 'full') ?? options.packages.find((o) => o.length === 'half');
  if (heldPackage) {
    return { action: 'package', length: heldPackage.length, packageId: heldPackage.pkg.id, waiveReason: '' };
  }
  const length: LatePickupLength = options.full.available || !options.half.available ? 'full' : 'half';
  return { action: 'charge', length, packageId: null, waiveReason: '' };
}

/** The package a choice resolves to: the picked one, else the first listed. */
export function resolveLatePickupPackage(
  packages: ReadonlyArray<LatePickupPackageOption>,
  choice: LatePickupChoice,
): LatePickupPackageOption | null {
  if (packages.length === 0) return null;
  if (choice.packageId) return packages.find((o) => o.pkg.id === choice.packageId) ?? packages[0] ?? null;
  return packages[0] ?? null;
}

/**
 * The disposition the checkout POST carries, or null when the choice is not
 * yet valid (a waiver with no note, a length this location has no price
 * for). Returning null rather than a half-formed body is what stops the desk
 * from hitting a 4xx it cannot read.
 */
export function latePickupDisposition(
  choice: LatePickupChoice,
  proposal: AdminLatePickup,
):
  | { action: 'charge'; length: LatePickupLength }
  | { action: 'package'; packageId: string; length: LatePickupLength }
  | { action: 'waive'; reason: string }
  | null {
  const options = latePickupOptions(proposal);
  if (choice.action === 'charge') {
    return options[choice.length].available ? { action: 'charge', length: choice.length } : null;
  }
  if (choice.action === 'waive') {
    const reason = choice.waiveReason.trim();
    return reason.length >= 2 ? { action: 'waive', reason } : null;
  }
  const picked = resolveLatePickupPackage(options.packages, choice);
  return picked ? { action: 'package', packageId: picked.pkg.id, length: picked.length } : null;
}

function formatMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function dogsWord(n: number): string {
  return `${n} dog${n === 1 ? '' : 's'}`;
}

/** "$20.00" for one dog; "$20.00 × 2 dogs = $40.00" for two. */
function priceText(price: LatePickupPrice, dogs: number): string {
  if (dogs <= 1) return formatMoney(price.totalCents);
  return `${formatMoney(price.perDogCents)} × ${dogsWord(dogs)} = ${formatMoney(price.totalCents)}`;
}

function packageLabel(o: LatePickupPackageOption): string {
  return o.pkg.label ?? `${o.pkg.totalDays}-day ${o.length === 'half' ? 'half-day' : 'daycare'} package`;
}

function packageOptionText(o: LatePickupPackageOption, daysNeeded: number): string {
  const day = o.length === 'half' ? 'half' : 'full';
  return `${daysNeeded} ${day} day${daysNeeded === 1 ? '' : 's'} from ${packageLabel(o)} · ${o.pkg.remainingDays} of ${o.pkg.totalDays} days left`;
}

export interface LatePickupControlProps {
  /** null while the proposal is still loading. */
  readonly proposal: AdminLatePickup | null;
  /** True while the read is in flight — renders the skeleton, not a spinner. */
  readonly loading?: boolean;
  /** Set when the read failed; the row says so and offers Retry. */
  readonly error?: string | null;
  readonly onRetry?: () => void;
  /**
   * #1157 E2.5 — set when the desk's corrected checkout time took a late
   * pickup back to on time. The row says so (the charge "goes away" visibly)
   * instead of the offer silently disappearing.
   */
  readonly cleared?: { readonly correctedLabel: string; readonly graceLabel: string } | null;
  readonly choice: LatePickupChoice;
  readonly disabled?: boolean;
  /** Scopes test ids by booking id. */
  readonly testIdSuffix: string;
  readonly onChange: (next: LatePickupChoice) => void;
}

export function LatePickupControl({
  proposal,
  loading = false,
  error = null,
  onRetry,
  cleared = null,
  choice,
  disabled = false,
  testIdSuffix,
  onChange,
}: LatePickupControlProps): JSX.Element | null {
  const suffix = `-${testIdSuffix}`;
  const groupName = useId();
  const waiveId = `late-pickup-waive-reason${suffix}`;
  const pickerId = `late-pickup-package${suffix}-picker`;

  if (loading) {
    return (
      <div
        className="ferro-late-pickup ferro-late-pickup--loading"
        data-testid={`late-pickup-loading${suffix}`}
        aria-busy="true"
      >
        <span className="ferro-late-pickup__skeleton" />
      </div>
    );
  }

  // Principle 5 — a failed read is NOT "no late charge". Say what happened
  // and how to recover; never let an outage quietly waive a charge.
  if (error) {
    return (
      <div
        className="ferro-banner ferro-banner--warning ferro-late-pickup"
        role="status"
        data-testid={`late-pickup-error${suffix}`}
      >
        <p className="ferro-late-pickup__line">
          Could not check the boarding checkout time for this dog, so no late
          pickup is being proposed. Check the pickup time before you collect.
        </p>
        {onRetry ? (
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={onRetry}
            data-testid={`late-pickup-retry${suffix}`}
          >
            Retry
          </button>
        ) : null}
      </div>
    );
  }

  // #1157 E2.5 — the clock said late, the corrected checkout time says on
  // time: no charge is offered or sent, and the row says why.
  if (cleared && proposal && !proposal.late) {
    return (
      <p
        className="ferro-field__hint ferro-late-pickup__cleared"
        role="status"
        data-testid={`late-pickup-cleared${suffix}`}
      >
        Checkout corrected to {cleared.correctedLabel}, before the {cleared.graceLabel} cutoff —
        no late pickup charge.
      </p>
    );
  }

  // Not applicable (not boarding, not the last day, the owner has the rule
  // off) or simply not late yet: the row shows nothing at all. A dog
  // collected on time is not an exception and must not look like one.
  if (!proposal || !proposal.applicable || !proposal.late) return null;

  const options = latePickupOptions(proposal);
  const chosenPackage = resolveLatePickupPackage(options.packages, choice);
  const waiveReadyError =
    choice.action === 'waive' && choice.waiveReason.trim().length < 2;

  const lengthRadio = (length: LatePickupLength) => {
    const price = options[length];
    if (!price.available) return null;
    return (
      <label className="ferro-field ferro-field--inline ferro-late-pickup__option">
        <input
          type="radio"
          name={groupName}
          checked={choice.action === 'charge' && choice.length === length}
          disabled={disabled}
          onChange={() => onChange({ ...choice, action: 'charge', length })}
          data-testid={`late-pickup-${length}${suffix}`}
        />
        <span>
          {length === 'half' ? 'Half day' : 'Full day'} of daycare — {priceText(price, options.dogCount)}
        </span>
      </label>
    );
  };

  return (
    <div
      className="ferro-banner ferro-banner--warning ferro-late-pickup"
      role="group"
      aria-label="Late pickup"
      data-testid={`late-pickup${suffix}`}
    >
      <p className="ferro-late-pickup__line">
        <strong data-testid={`late-pickup-flag${suffix}`}>Late pickup</strong> — collected{' '}
        {proposal.nowLabel}, after the {proposal.graceLabel} cutoff (checkout is{' '}
        {proposal.checkoutLabel}).
        {options.dogCount > 1 ? ` Charged per dog: ${dogsWord(options.dogCount)}.` : null}
      </p>

      {lengthRadio('half')}
      {lengthRadio('full')}

      {chosenPackage ? (
        <>
          <label className="ferro-field ferro-field--inline ferro-late-pickup__option">
            <input
              type="radio"
              name={groupName}
              checked={choice.action === 'package'}
              disabled={disabled}
              onChange={() =>
                onChange({
                  ...choice,
                  action: 'package',
                  packageId: chosenPackage.pkg.id,
                  length: chosenPackage.length,
                })
              }
              data-testid={`late-pickup-package${suffix}`}
            />
            <span>Use package — {packageOptionText(chosenPackage, options.daysNeeded)}</span>
          </label>
          {options.packages.length > 1 ? (
            <label className="ferro-field ferro-late-pickup__picker" htmlFor={pickerId}>
              <span className="ferro-field__label">Which package</span>
              <select
                id={pickerId}
                className="ferro-field__input"
                value={chosenPackage.pkg.id}
                disabled={disabled || choice.action !== 'package'}
                onChange={(e) => {
                  const next = options.packages.find((o) => o.pkg.id === e.target.value);
                  onChange({ ...choice, packageId: e.target.value, length: next?.length ?? choice.length });
                }}
                data-testid={pickerId}
              >
                {options.packages.map((o) => (
                  <option key={o.pkg.id} value={o.pkg.id}>
                    {o.length === 'half' ? 'Half day' : 'Full day'} · {packageLabel(o)} ·{' '}
                    {o.pkg.remainingDays} of {o.pkg.totalDays} left
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </>
      ) : (
        <p
          className="ferro-field__hint ferro-late-pickup__line"
          data-testid={`late-pickup-no-package${suffix}`}
        >
          {options.shortPackages > 0
            ? `This account's daycare package has fewer than ${options.daysNeeded} days left — not enough for ${dogsWord(options.dogCount)}.`
            : 'No active daycare package on this account.'}
        </p>
      )}

      <label className="ferro-field ferro-field--inline ferro-late-pickup__option">
        <input
          type="radio"
          name={groupName}
          checked={choice.action === 'waive'}
          disabled={disabled}
          onChange={() => onChange({ ...choice, action: 'waive' })}
          data-testid={`late-pickup-waive${suffix}`}
        />
        <span>Waive it</span>
      </label>

      {choice.action === 'waive' ? (
        <label className="ferro-field ferro-late-pickup__picker" htmlFor={waiveId}>
          <span className="ferro-field__label">Why (goes on the invoice)</span>
          <input
            id={waiveId}
            className="ferro-field__input"
            type="text"
            value={choice.waiveReason}
            maxLength={200}
            disabled={disabled}
            placeholder="e.g. our grooming ran long"
            onChange={(e) => onChange({ ...choice, waiveReason: e.target.value })}
            data-testid={`late-pickup-waive-reason${suffix}`}
          />
          {waiveReadyError ? (
            <span
              className="ferro-field__hint"
              data-testid={`late-pickup-waive-hint${suffix}`}
            >
              Say why, so the invoice explains itself.
            </span>
          ) : null}
        </label>
      ) : null}

      <p className="ferro-field__hint ferro-late-pickup__line" data-testid={`late-pickup-adjust-hint${suffix}`}>
        Need a different amount? Use Adjust price on the invoice after checkout.
      </p>
    </div>
  );
}
